import { useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { DataRequestForm } from './DataRequestForm';

export interface DataRequestBoxProps {
  /** Button and modal copy. "Request new data" browsing, "Missing data? Tell
   *  us" on a SKU. */
  readonly label: string;
  readonly placeholder: string;
  /** The SKU the reader was looking at, when there is one. */
  readonly sku?: string;
  /** Signed-in surfaces post with the customer's credential. */
  readonly signedIn?: boolean;
  /** Placement classes for the trigger button. */
  readonly className?: string;
}

/**
 * "Request new data": one text box for the API a reader wanted and did not find.
 *
 * The trigger sits in each surface's header row, where a reader is already
 * looking, and the message opens in a modal so the page underneath does not
 * reflow around a form. Closed, the whole component is one button: a prerendered
 * marketing page renders identical markup on the server and the first client
 * pass, because the modal's contents do not exist until it opens.
 */
export const DataRequestBox = ({
  label,
  placeholder,
  sku,
  signedIn = false,
  className = '',
}: DataRequestBoxProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="line" className={className}>
          <Icon name="sparkles" size={15} className="text-accent" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>
        <DataRequestForm
          placeholder={placeholder}
          sku={sku}
          signedIn={signedIn}
          onClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
